import React from 'react';
import { SectionWrapper } from '../ui/SectionWrapper';
import { Scale, Target, GitMerge, ShieldCheck, ArrowRight } from 'lucide-react';
const steps = [{
  num: '01',
  title: 'Deep Research',
  desc: 'We analyze contracts, pay structures, and software capabilities.'
}, {
  num: '02',
  title: 'Framework Check',
  desc: 'Applied against our standardized evaluation criteria.'
}, {
  num: '03',
  title: 'Risk Analysis',
  desc: 'Identifying failure modes and hidden tradeoffs.'
}, {
  num: '04',
  title: 'Publication',
  desc: 'Transparent, unbiased guides for the community.'
}];
export function HowEvaluationWorksSection() {
  return <SectionWrapper background="muted" id="methodology" data-id="element-195">
      <div className="text-center mb-12" data-id="element-196">
        <h2 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-197">
          Our Methodology
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-198">
          We don't issue "good" or "bad" scores. We evaluate fit, risk, and
          failure modes based on a rigorous framework.
        </p>
      </div>

      {/* Process Diagram */}
      <div className="hidden md:grid grid-cols-4 gap-4 mb-16 relative" data-id="element-199">
        {/* Connecting Line */}
        <div className="absolute top-6 left-0 w-full h-0.5 bg-slate-200 -z-10" data-id="element-200" />

        {steps.map((step, i) => <div key={i} className="text-center bg-slate-50 px-2" data-id="element-201">
            <div className="w-12 h-12 bg-white border-2 border-teal-600 rounded-full flex items-center justify-center text-teal-700 font-bold mx-auto mb-4 shadow-sm" data-id="element-202">
              {step.num}
            </div>
            <h3 className="font-bold text-slate-900 mb-2 text-sm" data-id="element-203">
              {step.title}
            </h3>
            <p className="text-xs text-slate-500 leading-relaxed" data-id="element-204">
              {step.desc}
            </p>
          </div>)}
      </div>

      {/* Mobile Process Stack */}
      <div className="md:hidden space-y-6 mb-12" data-id="element-205">
        {steps.map((step, i) => <div key={i} className="flex gap-4" data-id="element-206">
            <div className="w-10 h-10 bg-white border-2 border-teal-600 rounded-full flex items-center justify-center text-teal-700 font-bold flex-shrink-0" data-id="element-207">
              {step.num}
            </div>
            <div data-id="element-208">
              <h3 className="font-bold text-slate-900 mb-1 text-sm" data-id="element-209">
                {step.title}
              </h3>
              <p className="text-xs text-slate-500" data-id="element-210">{step.desc}</p>
            </div>
          </div>)}
      </div>

      {/* Principles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-211">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-id="element-212">
          <Target className="w-5 h-5 text-slate-400 mb-3" data-id="element-213" />
          <h4 className="font-bold text-slate-900 text-sm mb-2" data-id="element-214">
            Incentives Matter
          </h4>
          <p className="text-xs text-slate-500" data-id="element-215">
            We track how compensation shapes behavior.
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-id="element-216">
          <GitMerge className="w-5 h-5 text-slate-400 mb-3" data-id="element-217" />
          <h4 className="font-bold text-slate-900 text-sm mb-2" data-id="element-218">
            Systems &gt; Talent
          </h4>
          <p className="text-xs text-slate-500" data-id="element-219">
            We evaluate the model, not just the founder.
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-id="element-220">
          <Scale className="w-5 h-5 text-slate-400 mb-3" data-id="element-221" />
          <h4 className="font-bold text-slate-900 text-sm mb-2" data-id="element-222">Tradeoffs</h4>
          <p className="text-xs text-slate-500" data-id="element-223">
            High pay means high risk. We make it explicit.
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-id="element-224">
          <ShieldCheck className="w-5 h-5 text-slate-400 mb-3" data-id="element-225" />
          <h4 className="font-bold text-slate-900 text-sm mb-2" data-id="element-226">
            Standardized
          </h4>
          <p className="text-xs text-slate-500" data-id="element-227">
            Consistent criteria for fair comparison.
          </p>
        </div>
      </div>

      <div className="mt-10 text-center" data-id="element-228">
        <a href="#" className="inline-flex items-center text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors" data-id="element-229">
          Read Full Methodology <ArrowRight className="ml-1 w-4 h-4" data-id="element-230" />
        </a>
      </div>
    </SectionWrapper>;
}